/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {memo} from 'react';
import {useSelector} from 'react-redux';
import {DeviceActionDialogs as ApplicationsDeviceActionDialogs} from '@ion/applications/src/pages/devices/DeviceActionDialogs';
import {
  devicesFiltersSelector,
  devicesIncludeSubAccountsSelector,
  devicesSearchSelector,
  devicesTotalRowsCountSelector
} from '@ion/applications/src/pages/devices/state-selectors';
import {CRUDPermissions, GenericObject} from '@ion/core/src/types/entities';
import {AssetTreeRefresh} from '@ion/dls/src/editors/AssetEditor';
import useModularSearch from '../../routes/provisioning/devicefiles/useModularSearch';

type DeviceActionDialogsProps = AssetTreeRefresh & {
  selectedJobId: (jobId: number) => void;
  isToggleSwitchGrid?: boolean;
  isShowColumnChooserIcon?: boolean;
  isShowRefreshButton?: boolean;
  includeGroupProperties?: boolean;
  assetsPermissions?: CRUDPermissions;
  devicesPermissions?: CRUDPermissions;
  hasCreateJob?: boolean;
  isDeviceNotes?: boolean;
  isCtcAdmin?: boolean;
  Features?: GenericObject;
  jobSearchEngineEnabled?: boolean;
  isVbuFwEnabled?: boolean;
};

/**
 * Wrapper for eliminating the need for props when using the device
 * dialogs so we can use freely throughout ion-ctc-admin.
 */
export const DeviceActionDialogs = ({
  selectedJobId,
  isToggleSwitchGrid,
  assetsPermissions,
  hasCreateJob,
  devicesPermissions,
  Features,
  showTreeRefresh = false,
  isDeviceNotes = false,
  isVbuFwEnabled = false,
  includeGroupProperties = false,
  isShowColumnChooserIcon = false,
  isShowRefreshButton = false,
  jobSearchEngineEnabled = false,
  isCtcAdmin
}: DeviceActionDialogsProps) => {
  const includeSubAccounts = useSelector(devicesIncludeSubAccountsSelector);
  const totalRowsCount     = useSelector(devicesTotalRowsCountSelector);
  const search             = useSelector(devicesSearchSelector);
  const filterChips        = useSelector(devicesFiltersSelector);

  const {mappings} = useModularSearch();

  return (
    <ApplicationsDeviceActionDialogs includeSubAccounts={includeSubAccounts}
      assetsPermissions={assetsPermissions}
      devicesPermissions={devicesPermissions}
      hasCreateJob={hasCreateJob}
      Features={Features}
      selectedJobId={selectedJobId}
      totalRowsCount={totalRowsCount}
      isVbuFwEnabled={isVbuFwEnabled}
      isDeviceNotes={isDeviceNotes}
      mappings={mappings}
      search={search}
      filters={filterChips}
      includeGroupProperties={includeGroupProperties}
      includeDeviceGridCheckboxes
      includeAssetGridCheckboxes
      isToggleSwitchGrid={isToggleSwitchGrid}
      isShowRefreshButton={isShowRefreshButton}
      isCtcAdmin={isCtcAdmin}
      isShowColumnChooserIcon={isShowColumnChooserIcon}
      jobSearchEngineEnabled={jobSearchEngineEnabled}
      showTreeRefresh={showTreeRefresh} />
  );
};

export default memo(DeviceActionDialogs);
